import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@noths/polaris-design-system-core-components';
import dynamic from 'next/dynamic';

import type { HeroVideoButton } from 'src/components/molecules/HeroVideo/HeroVideo';
import {
  CloseIcon,
  PauseIcon,
  PlayIcon,
  ReplayIcon,
  SoundOffIcon,
  SoundOnIcon,
} from 'src/components/molecules/HeroVideo/icons';
import * as styles from './HeroVideoTakeover.styles';

const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), { ssr: false });

export interface HeroVideoTakeoverProps {
  buttons: HeroVideoButton[];
  endScreenHeading?: string;
  expandedPlaybackId: string;
  onCollapse: () => void;
  onCtaClick?: (button: HeroVideoButton, e: React.MouseEvent<HTMLAnchorElement>) => void;
  onVideoComplete?: () => void;
  /** Reported to Mux Data so views can be filtered by film. */
  videoTitle: string;
}

const CONTROLS_HIDE_DELAY_MS = 5000;

export const HeroVideoTakeover = ({
  buttons,
  endScreenHeading,
  expandedPlaybackId,
  onCollapse,
  onCtaClick,
  onVideoComplete,
  videoTitle,
}: HeroVideoTakeoverProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [ended, setEnded] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  // Remounting the player (key bump) is the simplest reliable restart with the declarative API.
  const [playthrough, setPlaythrough] = useState(0);

  // The overlay doubles as the pause screen (per the CMS field instruction).
  const overlayVisible = paused || ended;

  const showControls = () => {
    setControlsVisible(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_DELAY_MS);
  };

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    containerRef.current?.focus();
    showControls();

    return () => {
      document.body.style.overflow = previousOverflow;
      clearTimeout(hideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While the overlay shows, the controls must not fade away beneath it.
  useEffect(() => {
    if (overlayVisible) {
      clearTimeout(hideTimerRef.current);
      setControlsVisible(true);
    }
  }, [overlayVisible]);

  const restart = () => {
    setEnded(false);
    setPaused(false);
    setPlaythrough((count) => count + 1);
  };

  const togglePause = () => {
    if (ended) {
      restart();

      return;
    }

    setPaused((wasPaused) => !wasPaused);
  };

  const playPauseLabel = ended ? 'Replay' : paused ? 'Play' : 'Pause';

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- modal dialog: Escape-to-close on the container, and pointer/key activity restores the faded controls
    <div
      aria-label={endScreenHeading ?? 'Hero video'}
      aria-modal="true"
      css={styles.container}
      data-controls-visible={controlsVisible}
      onFocus={showControls}
      onKeyDown={(event) => {
        showControls();

        if (event.key === 'Escape') {
          onCollapse();
        }
      }}
      onPointerMove={showControls}
      onTouchStart={showControls}
      ref={containerRef}
      role="dialog"
      tabIndex={-1}
    >
      <MuxPlayer
        autoPlay
        css={styles.player}
        key={playthrough}
        maxResolution="1080p"
        metadata={{ player_name: 'browse-cmspages', video_title: videoTitle }}
        muted={muted}
        nohotkeys
        onEnded={() => {
          setEnded(true);
          onVideoComplete?.();
        }}
        // Mirror the media rather than assume it: iOS backgrounding, headphones disconnecting or a
        // refused autoplay all pause the media without us, and the button must reflect that.
        onPause={() => setPaused(true)}
        onPlay={() => {
          setPaused(false);
          setEnded(false);
        }}
        onTimeUpdate={(event: { target: { currentTime: number; duration: number } }) => {
          const { currentTime, duration } = event.target;

          if (duration > 0) {
            setProgress((currentTime / duration) * 100);
          }
        }}
        paused={paused}
        playbackId={expandedPlaybackId}
        playsInline
        streamType="on-demand"
      />
      {overlayVisible && (
        <div css={styles.overlay}>
          <div css={styles.overlayContent}>
            {endScreenHeading && <h2 css={styles.overlayHeading}>{endScreenHeading}</h2>}
            {buttons.map((button, index) => (
              <Button
                fullWidth
                href={button.href}
                // Over the dark scrim the transparent `secondary` variant loses contrast; the
                // Figma end screen uses a solid white pill with purple text, which is `inverted`.
                inverted={index > 0}
                key={button.href}
                onClick={(e) => onCtaClick?.(button, e)}
              >
                {button.text}
              </Button>
            ))}
            <Button fullWidth inverted onClick={restart} type="button">
              Watch again
            </Button>
          </div>
        </div>
      )}
      <div css={styles.controls}>
        <button
          aria-label="Close video"
          css={styles.closeButton}
          onClick={onCollapse}
          type="button"
        >
          <CloseIcon />
        </button>
        <button
          aria-label={playPauseLabel}
          css={styles.playPauseButton}
          onClick={togglePause}
          type="button"
        >
          {ended ? (
            <ReplayIcon size={20} />
          ) : paused ? (
            <PlayIcon size={20} />
          ) : (
            <PauseIcon size={20} />
          )}
        </button>
        <button
          aria-label={muted ? 'Unmute' : 'Mute'}
          css={styles.muteButton}
          onClick={() => setMuted((wasMuted) => !wasMuted)}
          type="button"
        >
          {muted ? <SoundOffIcon size={20} /> : <SoundOnIcon size={20} />}
        </button>
        <div css={styles.progressTrack}>
          <div
            css={styles.progressFill}
            data-testid="hero-video-progress"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};
